import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Grid,
  Stack,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import SnackbarConfig from "../../utils/snackbarConfig";
import {
  getRequestAuthenticationRequest,
  postAuthenticationRequest,
} from "../../services/apiManager/apiRequests";
import apiConfig from "../../services/apiManager/apiConfig";
import ExitIcon from "../../assets/Icon/ExitIcon";
import EnterIcon from "../../assets/Icon/EnterIcon";
import {
  isMobileDeviceCheck,
  sendMessageToReactNative,
  truncateLocationName,
} from "../../utils";
import storePersist, { localConstant } from "../../services/localStorage";
import NavBar from "../../NavBar";
import MenuBar from "../menuBar";
import { useResponsive } from "../../hooks/useResponsive";
import BottomNav from "../../components/BottomNav/BottomNav";

const GatesListForAllLocations = () => {
  const { t } = useTranslation();
  const [gatesList, setGatesList] = useState([]);
  const [selectedGate, setSelectedGate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const lgUp = useResponsive("up", "lg");
  const [locationsList, setLocationsList] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [showGates, setShowGates] = useState(false);

  const fetchLocationsList = async () => {
    try {
      setLoading(true);
      const params = {
        "q.op": "OR",
        start: "0",
        rows: "10000",
        wt: "json",
        cl: "OPS",
        sort: "location_city asc",
      };
      const response = await getRequestAuthenticationRequest(
        apiConfig.reservationApis.locationSolrList,
        params,
      );
      if (response?.response?.docs) {
        setLocationsList(response?.response?.docs);
      } else {
        enqueueSnackbar("Failed to fetch location data", {
          variant: "error",
          autoHideDuration: 3000,
          action: SnackbarConfig,
        });
      }
    } catch (error) {
      enqueueSnackbar("Failed to fetch location data", {
        variant: "error",
        autoHideDuration: 3000,
        action: SnackbarConfig,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchLocationGatesList = async () => {
    try {
      setLoading(true);
      const params = {
        fq: "equipment_type_code:GATE&fq=equipment_status_code:ACTIVE&start=0&rows=10000",
      };
      const data = await getRequestAuthenticationRequest(
        apiConfig.reservationApis.locationGatestListSolr,
        params,
      );
      if (data?.response?.docs?.length) {
        const gates = groupGatesWithLocationId(data.response.docs);
        addLocationsNameInGatesList(gates, locationsList);
      } else {
        enqueueSnackbar("No Gates List Found", SnackbarConfig.error);
      }
    } catch (err) {
      enqueueSnackbar("Error Fetching Gates List", SnackbarConfig.error);
      setGatesList([]);
    } finally {
      setLoading(false);
    }
  };

  const addLocationsNameInGatesList = (gates, locations) => {
    const gatesListWithLocationName = gates?.map((gate) => {
      const matchedLocation = locations.find(
        (loc) => loc.location_id === gate.locationId,
      );

      if (matchedLocation) {
        return {
          ...gate,
          locationName: matchedLocation.location_name,
        };
      }
      return gate;
    });
    setGatesList(gatesListWithLocationName);
  };

  const groupGatesWithLocationId = (gatesData) => {
    return Object.values(
      gatesData.reduce((acc, gate) => {
        const locationId = gate.location_id;
        const locationName = locationId || "Unknown Location";

        if (!acc[locationId]) {
          acc[locationId] = {
            locationId,
            locationName,
            equipments: [],
          };
        }

        acc[locationId].equipments.push({
          equipmentId: gate.equipment_id,
          equipmentName: gate.equipment_name,
        });

        return acc;
      }, {}),
    );
  };

  useEffect(() => {
    fetchLocationsList();
  }, []);

  const handleLocationChange = (event) => {
    setSelectedLocation(event.target.value);
    setShowGates(false);
  };

  const handleShowGates = () => {
    if (selectedLocation) {
      fetchLocationGatesList();
      setShowGates(true);
    } else {
      enqueueSnackbar("Please select a location first", SnackbarConfig.error);
    }
  };

  const InGateTextColor = "primary.main";
  const InGateButtonStyles = {
    backgroundColor: "#fff",
    borderColor: "#8A8886",
    color: "#605E5C",
    width: "100%",
    height: "35px",
  };

  const GateActions = ({ openGate, gates }) => {
    const noOfItems = 2;
    const items = [];

    for (let i = 0; i < gates?.length; i += noOfItems) {
      const gate1 = gates[i];
      const gate2 = gates[i + 1];

      items.push(
        <Stack
          spacing={1}
          key={i}
          direction="row"
          sx={{ marginBottom: 1, marginTop: 1 }}
        >
          {gate1?.equipmentId && (
            <Button
              sx={{ mt: 2, mb: 2, mr: 1 }}
              size="large"
              variant="outlined"
              type="submit"
              style={InGateButtonStyles}
              endIcon={
                gate1.equipment_direction === "IN" ? (
                  <EnterIcon color="#1D5AB9" width="28px" height="28px" />
                ) : (
                  <ExitIcon color="#1D5AB9" width="28px" height="28px" />
                )
              }
              onClick={(event) => {
                event.stopPropagation();
                openGate(gate1);
              }}
            >
              <Typography
                variant="subtitle2"
                color={InGateTextColor}
                sx={{ whiteSpace: "nowrap", display: "inline-block" }}
              >
                {gate1.equipmentName}
              </Typography>
            </Button>
          )}
          {gate2?.equipmentId && (
            <Button
              sx={{ mt: 2, mb: 2, ml: 1 }}
              size="large"
              variant="outlined"
              type="submit"
              style={InGateButtonStyles}
              endIcon={
                gate2.equipment_direction === "IN" ? (
                  <EnterIcon color="#1D5AB9" width="28px" height="28px" />
                ) : (
                  <ExitIcon color="#1D5AB9" width="28px" height="28px" />
                )
              }
              onClick={(event) => {
                event.stopPropagation();
                openGate(gate2);
              }}
            >
              <Typography
                variant="subtitle2"
                color={InGateTextColor}
                sx={{ whiteSpace: "nowrap", display: "inline-block" }}
              >
                {gate2.equipmentName}
              </Typography>
            </Button>
          )}
        </Stack>,
      );
    }

    return items?.length ? items : null;
  };

  const handleOpenGate = (equipment) => {
    setLoading(true);
    setError("");
    setSelectedGate(equipment);
    if (!isMobileDeviceCheck()) {
      handleGateOpenWithLocation(equipment?.equipmentId, {
        userId: storePersist.get(localConstant.USER_ID),
        customerId: storePersist.get(localConstant.ENTITY_CUSTOMER_ID),
        gateName: equipment?.equipmentName,
      });
    } else {
      sendMessageToReactNative("Sending message to React Native for location");
      sendMessageToReactNative("GET_DEVICE_LOCATION");
    }
  };

  const handleGateOpenWithLocation = async (gateId, payload) => {
    try {
      sendMessageToReactNative(
        `call api to React Native ${JSON.stringify(
          payload,
        )} and gateid ${JSON.stringify(gateId)}`,
      );
      const response = await openGate(gateId, payload);
    } catch (err) {
      const errorMessage = err.message || "Failed to open gate";
      setError(errorMessage);
      enqueueSnackbar(errorMessage, SnackbarConfig.error);
    } finally {
      setLoading(false);
      setSelectedGate(null);
    }
  };

  const openGate = async (gateId, payload) => {
    try {
      const doOpenGateURL = `${apiConfig.reservationApis.openGateForAdmin}customer/${payload?.customerId}/gates/${gateId}/open?deviceLatitude=${payload?.deviceLatitude}&deviceLongitude=${payload?.deviceLongitude}`;
      // &fetchLatLongTimeInMillis=${params?.fetchLatLongTimeInMillis}
      const data = await postAuthenticationRequest(doOpenGateURL);
      enqueueSnackbar(`Gate ${payload?.gateName} opened`, {
        variant: "success",
        autoHideDuration: 3000,
        action: SnackbarConfig,
      });
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.errMsg || "Failed to Open Gates", {
        variant: "error",
        autoHideDuration: 3000,
        action: SnackbarConfig,
      });
    }
  };

  const onMessageFromRN = (message) => {
    try {
      const { action, payload } = JSON.parse(message);
      if (action === "User_Location" && selectedGate) {
        if (
          payload === null ||
          payload === undefined ||
          payload?.coords?.latitude === undefined ||
          payload?.coords?.longitude === undefined ||
          payload?.coords?.latitude === null ||
          payload?.coords?.longitude === null
        ) {
          sendMessageToReactNative(
            `Sending failure to React Native ${JSON.stringify(
              payload,
            )} and equipment ${JSON.stringify(selectedGate)}`,
          );
          enqueueSnackbar(
            "Failed to get location. Please try again.",
            SnackbarConfig.error,
          );
          setLoading(false);
          return;
        }

        sendMessageToReactNative(
          `Sending success to React Native ${JSON.stringify(
            payload,
          )} and equipment ${JSON.stringify(selectedGate)}`,
        );

        handleGateOpenWithLocation(selectedGate?.equipmentId, {
          userId: storePersist.get(localConstant.USER_ID),
          customerId: storePersist.get(localConstant.ENTITY_CUSTOMER_ID),
          deviceLatitude: payload.coords.latitude,
          deviceLongitude: payload.coords.longitude,
          gateName: selectedGate?.equipmentName,
        });
      }
    } catch (error) {
      console.error("Error parsing message:", error);
      enqueueSnackbar("Failed to process location data", SnackbarConfig.error);
      setLoading(false);
    }
  };

  window.onMessageFromRN = onMessageFromRN;
  return (
    <div
      style={{
        width: lgUp ? `calc(100% - ${240}px)` : "100%",
        marginLeft: lgUp ? "240px" : 0,
      }}
    >
      <NavBar title={t("pages.gatesList.pageHeading")} enableBack={false} />
      <Container maxWidth="md">
        <MenuBar
          activeTab={
            storePersist.get(localConstant.USER_PROFILE) === "OWNER" ? 7 : 6
          }
        />
        <Box sx={{ mt: 4, mb: 4 }}>
          <Paper elevation={3} sx={{ p: 4 }}>
            <Stack spacing={3}>
              <FormControl fullWidth>
                <InputLabel id="location-select-label">
                  Select Location
                </InputLabel>
                <Select
                  labelId="location-select-label"
                  id="location-select"
                  value={selectedLocation}
                  label="Select Location"
                  onChange={handleLocationChange}
                >
                  {locationsList.map((location) => (
                    <MenuItem
                      key={location.location_id}
                      value={location.location_id}
                    >
                      {truncateLocationName(location.location_name)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={handleShowGates}
                disabled={!selectedLocation}
              >
                Show Gates
              </Button>
            </Stack>
          </Paper>
        </Box>

        {showGates && gatesList?.length > 0 && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Paper elevation={3} sx={{ p: 2 }}>
              <Box sx={{ mt: 1 }}>
                <Grid container spacing={1}>
                  {gatesList
                    .filter(
                      (gateInfo) => gateInfo.locationId === selectedLocation,
                    )
                    .map((gateInfo) => (
                      <Grid item xs={12} key={gateInfo?.locationId}>
                        <Typography variant="h6" gutterBottom>
                          {truncateLocationName(gateInfo.locationName)}
                        </Typography>
                        <GateActions
                          openGate={handleOpenGate}
                          gates={gateInfo.equipments}
                        />
                      </Grid>
                    ))}
                </Grid>
              </Box>
            </Paper>
          </Box>
        )}
        {loading && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: lgUp ? "240px" : 0,
              width: lgUp ? `calc(100% - 240px)` : "100%",
              height: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.6)",
              zIndex: 9999,
            }}
          >
            <CircularProgress size={60} />
          </Box>
        )}
      </Container>
      <BottomNav activeTab={5} />
    </div>
  );
};

export default GatesListForAllLocations;
